// static/js/components/ui/GuidancePromptsPanel.jsx

import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Info } from 'lucide-react';

// This would eventually come from an API
import { AI_GUIDANCE_PROMPTS } from './aiGuidancePrompts';

const GuidancePromptsPanel = ({ onSelectPrompt, isOpen, onClose }) => {
  const [expandedCategories, setExpandedCategories] = useState({});
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  
  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };
  
  // Handle prompt selection
  const handleSelectPrompt = (prompt) => {
    // Passes the prompt to the parent component
    // The toggling logic now happens in the parent's handleSelectGuidance function
    setSelectedPrompt(prompt);
    onSelectPrompt(prompt);
  };
  
  // Group prompts by category
  const promptsByCategory = AI_GUIDANCE_PROMPTS.reduce((acc, prompt) => {
    if (!acc[prompt.category]) {
      acc[prompt.category] = [];
    }
    acc[prompt.category].push(prompt);
    return acc;
  }, {});
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-primary-bg border border-primary-border rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-primary-border flex justify-between items-center">
          <h2 className="text-lg font-semibold text-primary-text">AI Guidance Prompts</h2>
          <button 
            onClick={onClose}
            className="text-primary-text hover:text-primary-button"
          >
            &times;
          </button>
        </div>
    
        <div className="overflow-y-auto p-4 flex-grow">
          {Object.entries(promptsByCategory).map(([category, prompts]) => (
            <div key={category} className="mb-6">
              <button
                onClick={() => toggleCategory(category)}
                className="flex items-center gap-2 text-primary-text hover:text-primary-button w-full text-left mb-2"
              >
                {expandedCategories[category] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                <span className="font-medium">{category}</span>
              </button>
          
              {expandedCategories[category] && (
                <div className="ml-6 space-y-4">
                  {prompts.map((prompt) => (
                    <div 
                      key={prompt.name}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedPrompt?.name === prompt.name 
                          ? 'bg-primary-700/30 border border-primary-button' 
                          : 'bg-primary-700/10 hover:bg-primary-700/20 border border-transparent'
                      }`}
                      onClick={() => handleSelectPrompt(prompt)}
                    >
                      <h3 className="font-medium text-primary-text mb-1">{prompt.name}</h3>
                      <p className="text-sm text-primary-text/80 mb-2">{prompt.short_description}</p>

                        {/* More Details button in AI Guidance Prompts panel */}
                        <details className="text-xs text-primary-text/70">
                          <summary 
                            className="cursor-pointer hover:text-primary-button inline-flex items-center"
                            onClick={(e) => {
                              // Stops propagation to prevent the panel from closing when More Details pressed
                              e.stopPropagation();
                            }}
                          >
                            <Info size={12} className="mr-1" />
                            More details
                          </summary>
                          <p className="mt-2 pl-4 border-l-2 border-primary-border">{prompt.long_description}</p>
                        </details>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
    
        <div className="p-4 border-t border-primary-border">
          {selectedPrompt ? (
            <div className="text-sm">
              <span className="text-primary-text/70">Selected:</span> 
              <span className="ml-2 text-primary-button font-medium">{selectedPrompt.name}</span>
            </div>
          ) : (
            <div className="text-sm text-primary-text/70">Select a guidance prompt to customize AI responses</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuidancePromptsPanel;
